import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ArrowDownWideNarrow, ChevronDown, Settings2 } from 'lucide-react';
import Button from '../Button';
import { CountBadge } from '../Badge';
import { MOBILE_QUERY } from '../utils/breakpoints';
import FilterPopover from './FilterPopover';
import FilterOptionList from './FilterOptionList';
import FilterRange from './FilterRange';
import FilterDrawer from './FilterDrawer';
import FilterSortSheet from './FilterSortSheet';
import FilterChips from './FilterChips';
import DEFAULT_LABELS from './filterLabels';
import {
  activeChips,
  clearAttribute,
  countActive,
  countForAttribute,
  normalizeValue,
  removeChip,
  setRange,
  toggleOption,
} from './filterLogic';
import './Filter.css';

/**
 * Filter — Design System
 *
 * The toolbar above a product listing: filter attributes on the left, sort on
 * the right. Figma (MCP Version): component 4411:18956 — Desktop 4411:18954,
 * Mobile 4411:18955. Only the closed bar is drawn there; the open states are
 * composed from DS parts:
 *
 * - Desktop (≥1024px): one tertiary text trigger per attribute (divided by
 *   24px rules). Each opens a panel in the DS Input dropdown look under it —
 *   option rows with check boxes (a search field above long lists) or a price
 *   range (RangeSlider + From / To). Choices APPLY AT ONCE. Sort opens the
 *   same panel with single-choice rows; a pick applies and closes. More
 *   attributes than `maxInline` fold into an "All filters" trigger that opens
 *   the drawer as a side panel.
 * - Mobile: "Filters" opens a bottom sheet with every attribute; choices are a
 *   draft until "Show N products" (`countResults`). The sort trigger shows the
 *   current order's name (lucide ArrowDownWideNarrow icon after it; "Sort by" while none) and opens a sheet of
 *   radio buttons; a pick applies and closes.
 * - Active choices show as a count on each trigger (on phones: one count on
 *   "Filters") and, on desktop only, as removable chips under the bar
 *   (`showChips`) with "Clear all" — on phones the drawer clears (Clear all +
 *   a Clear per section). A polite live region announces the result count.
 * - `sticky`: the bar sticks under the header (--header-height); once stuck,
 *   scrolling down carries it away with the page (scroll-linked, no timed
 *   animation) and scrolling up brings it back — never while a panel is open.
 *
 * Controlled: `value` ({ [attributeId]: string[] | [min, max] }) + `onChange`,
 * `sort` + `onSortChange`. The pure helpers that go with it — applyFilters,
 * facetCounts, sortItems, parseFilterQuery / buildFilterQuery (URL) — are
 * exported from the same entry. Brand-neutral: copy comes through `labels`.
 */

const SORT_ID = '__sort';
/**
 * Scroll-linked slide-away of the sticky bar. Once stuck, the bar keeps
 * moving with the page while scrolling down — pixel for pixel, as if it were
 * not sticky — until it is out of sight; scrolling up brings it back the same
 * way. No timed animation, and a bar still in the flow (not stuck yet) is never
 * moved. The offset is written straight to the element (no re-render per
 * frame); the returned flag only says "fully away" (ds-filter--hidden).
 * `enabled` false (not sticky, or a panel / sheet is open) snaps it back.
 */
const useScrollAway = (ref, enabled) => {
  const [away, setAway] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const win = el.ownerDocument.defaultView;
    let offset = 0;
    const apply = (next, max) => {
      offset = next;
      // `transform: none` at rest — a transform makes the bar the containing
      // block of anything fixed inside it
      el.style.transform = offset > 0 ? `translateY(${-offset}px)` : '';
      setAway(max > 0 && offset >= max);
    };
    if (!enabled) {
      apply(0, 0);
      return undefined;
    }
    let lastY = win.scrollY;
    // The flow top of the last frame while the bar was in the flow (null once
    // stuck): on the frame it sticks, only the scroll past that point counts
    let lastFlowTop = null;
    let stuck = false;
    let frame = 0;
    let pending = false;
    const measure = () => {
      pending = false;
      const y = win.scrollY;
      const dy = y - lastY;
      lastY = y;
      const top = parseFloat(win.getComputedStyle(el).top) || 0;
      // Where the bar sits without its own offset: at `top` = stuck
      const flowTop = el.getBoundingClientRect().top + offset;
      const max = top + el.offsetHeight;
      if (flowTop > top + 1 || y <= 0) {
        stuck = false;
        lastFlowTop = flowTop;
        apply(0, max);
        return;
      }
      const moved = stuck ? dy : (lastFlowTop === null ? 0 : dy - (lastFlowTop - top));
      stuck = true;
      apply(Math.min(max, Math.max(0, offset + moved)), max);
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      frame = win.requestAnimationFrame(measure);
    };
    win.addEventListener('scroll', schedule, { passive: true });
    win.addEventListener('resize', schedule);
    return () => {
      if (pending) win.cancelAnimationFrame(frame);
      win.removeEventListener('scroll', schedule);
      win.removeEventListener('resize', schedule);
    };
  }, [ref, enabled]);
  return away;
};

const defaultFormatValue = (v, attribute) => (attribute?.unit ? `${v} ${attribute.unit}` : String(v));

/* The window the bar lives in — not always the global one (a preview iframe) */
const viewOf = (el) => el?.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : null);

const isMobileFor = (el) => {
  const view = viewOf(el);
  return Boolean(view && typeof view.matchMedia === 'function' && view.matchMedia(MOBILE_QUERY).matches);
};

const Filter = ({
  attributes,
  value = {},
  onChange,
  sortOptions = [],
  sort = null,
  onSortChange,
  resultCount,
  countResults,
  getCounts,
  maxInline = 4,
  sticky = false,
  showChips = true,
  formatValue,
  labels: labelsProp,
  className = '',
  ...rest
}) => {
  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelsProp }), [labelsProp]);
  const baseId = useId().replace(/:/g, '');
  const applied = useMemo(() => normalizeValue(attributes, value), [attributes, value]);

  const [openId, setOpenId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const itemRefs = useRef({});
  const rootRef = useRef(null);
  // The sheets portal into the bar's own document body (set once mounted)
  const [portalTarget, setPortalTarget] = useState(null);
  useLayoutEffect(() => {
    setPortalTarget(rootRef.current?.ownerDocument?.body || null);
  }, []);

  const anyOpen = openId !== null || drawerOpen || sortSheetOpen;
  const hidden = useScrollAway(rootRef, sticky && !anyOpen);

  const fmt = useCallback(
    (v, attribute) => (typeof formatValue === 'function' ? formatValue(v, attribute) : defaultFormatValue(v, attribute)),
    [formatValue],
  );

  // A desktop panel belongs to the desktop bar: crossing the breakpoint closes it
  useEffect(() => {
    const view = viewOf(rootRef.current);
    if (!view || typeof view.matchMedia !== 'function') return undefined;
    const mq = view.matchMedia(MOBILE_QUERY);
    const close = () => setOpenId(null);
    if (mq.addEventListener) mq.addEventListener('change', close);
    else if (mq.addListener) mq.addListener(close);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', close);
      else if (mq.removeListener) mq.removeListener(close);
    };
  }, []);

  const emit = (next) => onChange?.(normalizeValue(attributes, next));

  const focusTrigger = (id) => {
    itemRefs.current[id]?.querySelector('.ds-filter__trigger')?.focus();
  };

  const togglePanel = (id) => setOpenId((current) => (current === id ? null : id));

  const closePanel = (id, reason) => {
    setOpenId((current) => (current === id ? null : current));
    if (reason === 'escape') focusTrigger(id);
  };

  const openDrawer = () => {
    setOpenId(null);
    setDrawerOpen(true);
  };

  const counts = getCounts ? getCounts(applied) : null;
  let total = null;
  if (typeof resultCount === 'number') total = resultCount;
  else if (typeof countResults === 'function') total = countResults(applied);

  const folded = attributes.length > maxInline;
  const inline = folded ? attributes.slice(0, maxInline) : attributes;
  const totalActive = countActive(attributes, applied);

  const chips = showChips
    ? activeChips(attributes, applied, { formatRange: ([a, b], attribute) => `${fmt(a, attribute)} – ${fmt(b, attribute)}` })
    : [];

  const sortLabel = sortOptions.find((o) => o.value === sort)?.label;

  const pickSort = (next, { repeat } = {}) => {
    if (!repeat && next !== sort) onSortChange?.(next);
    setOpenId(null);
    focusTrigger(SORT_ID);
  };

  const handleSortClick = () => {
    if (isMobileFor(rootRef.current)) {
      setOpenId(null);
      setSortSheetOpen(true);
    } else {
      togglePanel(SORT_ID);
    }
  };

  const renderPanel = (attribute) => {
    const n = countForAttribute(attribute, applied[attribute.id]);
    return (
      <>
        {attribute.type === 'range' ? (
          <FilterRange
            attribute={attribute}
            value={applied[attribute.id]}
            onCommit={(range) => emit(setRange(applied, attribute, range))}
            labels={labels}
            formatValue={fmt}
          />
        ) : (
          <div className="ds-filter__panel-body">
            <FilterOptionList
              name={`${baseId}-${attribute.id}`}
              legend={attribute.label}
              options={attribute.options || []}
              selected={applied[attribute.id] || []}
              multiple={attribute.multiple !== false}
              onToggle={(v) => emit(toggleOption(applied, attribute, v))}
              counts={counts ? counts[attribute.id] : undefined}
              searchable={attribute.searchable}
              labels={labels}
            />
          </div>
        )}
        <div className="ds-filter__panel-foot">
          <Button
            size="tertiary"
            variant="text"
            disabled={n === 0}
            onClick={() => emit(clearAttribute(applied, attribute.id))}
          >
            {labels.clear}
          </Button>
        </div>
      </>
    );
  };

  const rootClass = [
    'ds-filter',
    sticky && 'ds-filter--sticky',
    sticky && hidden && 'ds-filter--hidden',
    className,
  ].filter(Boolean).join(' ');

  const sortPanelId = `${baseId}-panel-sort`;

  const overlays = (
    <>
      <FilterDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApply={(next) => {
          onChange?.(next);
          setDrawerOpen(false);
        }}
        attributes={attributes}
        value={applied}
        getCounts={getCounts}
        countResults={countResults}
        formatValue={fmt}
        labels={labels}
        idBase={baseId}
      />
      {sortOptions.length > 0 && (
        <FilterSortSheet
          isOpen={sortSheetOpen}
          onClose={() => setSortSheetOpen(false)}
          onPick={(next) => {
            if (next !== sort) onSortChange?.(next);
            setSortSheetOpen(false);
          }}
          options={sortOptions}
          sort={sort}
          labels={labels}
          name={`${baseId}-sort-sheet`}
        />
      )}
    </>
  );

  return (
    <div ref={rootRef} className={rootClass} {...rest}>
      <div className="ds-filter__inner">
        <div className="ds-filter__bar">
          {/* Desktop: a trigger per attribute */}
          <div className="ds-filter__group ds-filter__group--desktop" role="group" aria-label={labels.filters}>
            {inline.map((attribute, index) => {
              const open = openId === attribute.id;
              const panelId = `${baseId}-panel-${attribute.id}`;
              return (
                <React.Fragment key={attribute.id}>
                  {index > 0 && <span className="ds-filter__divider" aria-hidden="true" />}
                  <div
                    className="ds-filter__item"
                    ref={(el) => { itemRefs.current[attribute.id] = el; }}
                  >
                    <Button
                      size="tertiary"
                      variant="text"
                      className={`ds-filter__trigger${open ? ' ds-filter__trigger--open' : ''}`}
                      iconRight={<ChevronDown size={20} className="ds-filter__chevron" />}
                      aria-haspopup="dialog"
                      aria-expanded={open}
                      aria-controls={open ? panelId : undefined}
                      onClick={() => togglePanel(attribute.id)}
                    >
                      <span className="ds-filter__trigger-label">{attribute.label}</span>
                      <CountBadge value={countForAttribute(attribute, applied[attribute.id])} className="ds-filter__count" />
                    </Button>
                    <FilterPopover
                      id={panelId}
                      open={open}
                      label={attribute.label}
                      variant={attribute.type === 'range' ? 'range' : 'list'}
                      onClose={(reason) => closePanel(attribute.id, reason)}
                    >
                      {renderPanel(attribute)}
                    </FilterPopover>
                  </div>
                </React.Fragment>
              );
            })}
            {folded && (
              <>
                <span className="ds-filter__divider" aria-hidden="true" />
                <div className="ds-filter__item">
                  <Button
                    size="tertiary"
                    variant="text"
                    className="ds-filter__trigger"
                    iconRight={<Settings2 size={20} />}
                    aria-haspopup="dialog"
                    aria-expanded={drawerOpen}
                    onClick={openDrawer}
                  >
                    <span className="ds-filter__trigger-label">{labels.allFilters}</span>
                    <CountBadge value={totalActive} className="ds-filter__count" />
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Mobile: one trigger for the drawer */}
          <div className="ds-filter__group ds-filter__group--mobile">
            <Button
              size="tertiary"
              variant="text"
              className="ds-filter__trigger"
              iconRight={<Settings2 size={20} />}
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              onClick={openDrawer}
            >
              <span className="ds-filter__trigger-label">{labels.filters}</span>
              <CountBadge value={totalActive} className="ds-filter__count" />
            </Button>
          </div>

          {sortOptions.length > 0 && (
            <div className="ds-filter__item ds-filter__sort" ref={(el) => { itemRefs.current[SORT_ID] = el; }}>
              <Button
                size="tertiary"
                variant="gray"
                className={`ds-filter__trigger ds-filter__trigger--sort${openId === SORT_ID ? ' ds-filter__trigger--open' : ''}`}
                iconRight={<ArrowDownWideNarrow size={20} />}
                aria-haspopup="dialog"
                aria-expanded={openId === SORT_ID || sortSheetOpen}
                aria-controls={openId === SORT_ID ? sortPanelId : undefined}
                aria-label={sortLabel ? `${labels.sort}: ${sortLabel}` : undefined}
                onClick={handleSortClick}
              >
                {/* The trigger reads the current order; "Sort by" only until one is set */}
                {sortLabel || labels.sort}
              </Button>
              <FilterPopover
                id={sortPanelId}
                open={openId === SORT_ID}
                label={labels.sort}
                align="end"
                onClose={(reason) => closePanel(SORT_ID, reason)}
              >
                <div className="ds-filter__panel-body">
                  <FilterOptionList
                    name={`${baseId}-sort`}
                    legend={labels.sort}
                    options={sortOptions}
                    selected={sort ? [sort] : []}
                    multiple={false}
                    deselectable={false}
                    searchable={false}
                    onToggle={pickSort}
                    labels={labels}
                  />
                </div>
              </FilterPopover>
            </div>
          )}
        </div>

        {chips.length > 0 && (
          <FilterChips
            chips={chips}
            onRemove={(chip) => emit(removeChip(applied, chip))}
            onClearAll={() => emit({})}
            labels={labels}
          />
        )}

        <p className="ds-filter__sr" role="status">
          {total === null ? '' : labels.results(total)}
        </p>
      </div>

      {/* The sheets leave the bar's stacking context (sticky + z-index), or
          they would sit under the site header. */}
      {portalTarget && createPortal(overlays, portalTarget)}
    </div>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  /** CSS colour of a swatch shown before the label (colour attributes) */
  swatch: PropTypes.string,
  /** Result count shown at the row's end; 0 disables the row (unless picked) */
  count: PropTypes.number,
  disabled: PropTypes.bool,
});

Filter.propTypes = {
  /**
   * The attributes, in bar order — plain data (the CMS shape):
   * { id, label, type: 'options', options[], multiple?, searchable?, field?, param? } |
   * { id, label, type: 'range', min, max, step?, unit?, field?, param? }.
   * `field` = the item key applyFilters reads; `param` = the URL query name.
   */
  attributes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['options', 'range']).isRequired,
    options: PropTypes.arrayOf(optionShape),
    multiple: PropTypes.bool,
    searchable: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    unit: PropTypes.string,
    field: PropTypes.string,
    param: PropTypes.string,
  })).isRequired,
  /** Applied choices: { [attributeId]: string[] | [min, max] } */
  value: PropTypes.object,
  /** Receives the next (normalised) value — desktop on every choice, mobile on "Show N products" */
  onChange: PropTypes.func,
  /** Sort choices; none → no sort trigger */
  sortOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  /** The current sort value */
  sort: PropTypes.string,
  onSortChange: PropTypes.func,
  /** Results for the applied value (the live region); defaults to countResults(value) */
  resultCount: PropTypes.number,
  /** (value) => number — the drawer's "Show N products" follows the draft through it */
  countResults: PropTypes.func,
  /** (value) => { [attributeId]: { [optionValue]: count } } — option counts (see facetCounts) */
  getCounts: PropTypes.func,
  /** Desktop triggers before the rest fold into "All filters" (Figma: 4) */
  maxInline: PropTypes.number,
  /** Stick under the header and slide away while scrolling down */
  sticky: PropTypes.bool,
  /** Removable chips for the active choices under the bar (desktop only — phones clear in the drawer) */
  showChips: PropTypes.bool,
  /** (number, attribute) => string — range values; default "200 kr" from attribute.unit */
  formatValue: PropTypes.func,
  /** Copy overrides — see filterLabels.js for the keys */
  labels: PropTypes.object,
  className: PropTypes.string,
};

export default Filter;
